import React, { useState, useEffect, useCallback } from 'react';
import { withRouter } from 'react-router';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import { withStyles } from '@material-ui/core';
import { compose, get, formatDate } from '../../utility';
import { withAppUser } from '../../shared/authorization/userConsumer';
import { ROLE_ID } from '../../constants';
import useRequest from '../../util/RequestEsign';
import { UnControlledTable } from '../../shared/table';
import styles from './superAdmin.styles';

function isSuperAdmin(user) {
  if (!user) return false;
  const nodes = get(user, 'systemAclsByUserId.nodes', []);
  return nodes.some(acl => Number(acl.roleId) === ROLE_ID.SUPER_ADMIN);
}

/** YYYY-MM-DD in UTC for calendar date boundaries (matches API). */
function formatUtcYmdFromDate(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayUtcYmd() {
  const now = new Date();
  return formatUtcYmdFromDate(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())));
}

/** Default "through" = today (UTC) + given days. */
function defaultThroughUtcPlusDays(days) {
  const now = new Date();
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + days));
  return formatUtcYmdFromDate(d);
}

const filterRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  marginBottom: 20,
  maxWidth: 640,
};

const dateValueTime = (v) => {
  if (v == null || v === '') return 0;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const compareDateValuesAsc = (a, b) => dateValueTime(a) - dateValueTime(b);

const compareDaysAsc = (a, b) => {
  const n1 = a === null || a === undefined ? 9999 : Number(a);
  const n2 = b === null || b === undefined ? 9999 : Number(b);
  return n1 - n2;
};

const makeColumns = (history) => [
  { Header: 'User', accessor: (row) => `${row.firstName || ''} ${row.lastName || ''}`.trim() || row.email, id: 'user', sortable: true },
  { Header: 'Email', accessor: 'email', sortable: true },
  {
    Header: 'Valid from',
    id: 'validFrom',
    accessor: (row) => row.validFrom || '',
    sortable: true,
    sortMethod: compareDateValuesAsc,
    Cell: ({ value }) => (value ? formatDate(value) : '—'),
  },
  {
    Header: 'Valid to',
    id: 'validTo',
    accessor: (row) => row.validTo || '',
    sortable: true,
    sortMethod: compareDateValuesAsc,
    Cell: ({ value }) => (value ? formatDate(value) : '—'),
  },
  {
    Header: 'Days left',
    id: 'daysToExpiry',
    accessor: (row) => (row.daysToExpiry != null ? row.daysToExpiry : ''),
    sortable: true,
    sortMethod: compareDaysAsc,
    Cell: ({ value }) => (value === '' || value === null || value === undefined ? '—' : String(value)),
  },
  {
    Header: 'Agent',
    id: 'link',
    sortable: false,
    Cell: ({ original }) => (
      <a
        href={`#/admin/agent-management/${original.userId}`}
        onClick={(e) => { e.preventDefault(); history.push(`/admin/agent-management/${original.userId}`); }}
      >
        View agent
      </a>
    ),
  },
];

function ExpiringSubscriptionsReportC({ classes, user, history }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [throughDate, setThroughDate] = useState(() => defaultThroughUtcPlusDays(30));

  const minDate = todayUtcYmd();
  const maxDate = defaultThroughUtcPlusDays(730);

  const fetchData = useCallback(async () => {
    if (!isSuperAdmin(user)) return;
    setLoading(true);
    try {
      const qs = `?through=${encodeURIComponent(throughDate)}`;
      const list = await useRequest({ endPoint: `/admin/reports/expiring-subscriptions${qs}` });
      setData(Array.isArray(list) ? list : []);
    } catch {
      setData([]);
    }
    setLoading(false);
  }, [user, throughDate]);

  useEffect(() => {
    if (!user) return;
    if (!isSuperAdmin(user)) {
      history.replace('/admin');
      return;
    }
    fetchData();
  }, [user, history, fetchData]);

  if (!user || !isSuperAdmin(user)) return null;

  return (
    <div className={classes.root}>
      <Typography className={classes.title} variant="title">Expiring subscriptions</Typography>
      <Typography color="textSecondary" style={{ marginBottom: 16, maxWidth: 720 }}>
        Show accounts whose subscription <strong>valid to</strong> (UTC calendar date) is from today through the date below, inclusive.
        Reach out proactively before renewal.
      </Typography>
      <div style={filterRowStyle}>
        <TextField
          id="expiring-through-date"
          label="Include expiring through (UTC)"
          type="date"
          value={throughDate}
          style={{ marginRight: 16, minWidth: 200 }}
          onChange={(e) => setThroughDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            min: minDate,
            max: maxDate,
          }}
        />
        <Typography variant="caption" color="textSecondary" style={{ paddingBottom: 8 }}>
          Default is today + 30 days (UTC). Adjust the date to widen or narrow the window.
        </Typography>
      </div>
      {loading ? (
        <Typography color="textSecondary">Loading…</Typography>
      ) : (
        <UnControlledTable
          data={data}
          columns={makeColumns(history)}
          defaultSorted={[{ id: 'validTo', desc: false }]}
        />
      )}
    </div>
  );
}

export default compose(
  withStyles(styles),
  withAppUser,
  withRouter
)(ExpiringSubscriptionsReportC);
